import { isDefined } from "@omni/utils/helper.utils";

import {
    clearFacetValues,
    clearHierarchicalFacet,
    setNumericFacet
} from "@/redux/modules/value/search/search.actions";
import {
    toHierarchicalFacet,
    toHierarchicalFacetPath
} from "@/services/bffSearch/responseTransformers/mergeHierarchicalFacets.transformer";
import { getTranslatedFacetName } from "@/utils/search/facetTranslation.utils";
import { getInvalidationsForComponent } from "@/utils/search/invalidation.utils";

import { FacetType } from "../../../facets/components/facet/@types/facetType.types";
import {
    mapToFacetValueInMillimetres,
    mapToFacetValueWithDisplayValue
} from "../../../facets/components/facet/components/UnitRangeFacet/unitRange.helpers";
import {
    FACET_DELIMITER,
    FACET_VALUE_STORE_STOCK_IDENTIFIER
} from "../../../facets/components/facet/facet.configuration";
import { SelectedFacet, SelectedFilterMapper } from "./selectedFacets.types";

const defaultFacetMapper: SelectedFilterMapper<FacetType.DEFAULT> = ({
    facet,
    filters,
    dispatch,
    component,
    t
}) =>
    filters.facetFilter?.selectedValues?.map((selectedFacetValue) => ({
        label: `${getTranslatedFacetName(
            facet.name,
            t
        )}: ${selectedFacetValue}`,
        onClear: () =>
            dispatch(
                clearFacetValues(
                    component,
                    facet.name,
                    [selectedFacetValue],
                    getInvalidationsForComponent(component)
                )
            )
    }));

const colorswatchFacetMapper: SelectedFilterMapper<FacetType.COLORSWATCH> = ({
    facet,
    filters,
    dispatch,
    component,
    t
}) =>
    facet.values
        .filter((facetValue) =>
            filters.facetFilter?.selectedValues.includes(facetValue.value)
        )
        .map((facetValue) => ({
            label: `${getTranslatedFacetName(facet.name, t)}: ${
                facetValue.name
            }`,
            onClear: () =>
                dispatch(
                    clearFacetValues(
                        component,
                        facet.name,
                        [facetValue.value],
                        getInvalidationsForComponent(component)
                    )
                )
        }));

const unitRangeFacetMapper: SelectedFilterMapper<FacetType.UNIT_RANGE> = ({
    facet,
    filters,
    dispatch,
    component,
    t
}) =>
    facet.values
        .filter((facetValue) =>
            filters.facetFilter?.selectedValues.includes(facetValue.value)
        )
        .map(mapToFacetValueInMillimetres)
        .map(mapToFacetValueWithDisplayValue)
        .map(({ displayValue, value }) => {
            const facetName = getTranslatedFacetName(facet.name, t);

            return {
                label: `${facetName}: ${displayValue}`,
                onClear: () =>
                    dispatch(
                        clearFacetValues(
                            component,
                            facet.name,
                            [value],
                            getInvalidationsForComponent(component)
                        )
                    )
            };
        });

const numericFacetMapper: SelectedFilterMapper<FacetType.NUMERIC> = ({
    facet,
    filters,
    dispatch,
    component,
    t
}) => {
    if (!filters.numericFilter) return;

    const numericFilter = filters.numericFilter;
    const lowerBound =
        numericFilter.lowerBound?.toString().replace(".", ",") ?? "*";
    const upperBound =
        numericFilter.upperBound?.toString().replace(".", ",") ?? "*";

    return [
        {
            label: `${getTranslatedFacetName(
                facet.name,
                t
            )}: ${lowerBound} - ${upperBound}`,
            onClear: () =>
                dispatch(
                    setNumericFacet(
                        component,
                        numericFilter.name,
                        undefined,
                        getInvalidationsForComponent(component)
                    )
                )
        }
    ];
};

const hierarchicalFacetMapper: SelectedFilterMapper<FacetType.HIERARCHICAL> = ({
    facet,
    filters,
    dispatch,
    component
}) => {
    const label = filters.facetFilter?.selectedValues.at(0);
    if (!label) return [];

    const parsedLabel = toHierarchicalFacet(
        toHierarchicalFacetPath(label).map(
            (lvl) => lvl.split(FACET_DELIMITER).at(2)!
        )
    );

    return [
        {
            label: parsedLabel,
            onClear: () => {
                dispatch(
                    clearHierarchicalFacet(
                        component,
                        facet.name,
                        getInvalidationsForComponent(component)
                    )
                );
            }
        }
    ];
};

const showProductsFacetMapper: SelectedFilterMapper<
    FacetType.SHOW_PRODUCTS
> = ({ facet, filters, dispatch, component, preferredStore, t }) => {
    const formatAsLabel = (facetValue: string) => {
        const [valueIdentifier] = facetValue.split(FACET_DELIMITER);

        if (valueIdentifier === FACET_VALUE_STORE_STOCK_IDENTIFIER) {
            if (isDefined(preferredStore)) {
                return (
                    t("search.facets.store.availableIn.active") +
                    " " +
                    preferredStore.shopName
                );
            } else {
                return undefined;
            }
        }

        return facetValue;
    };

    return filters.facetFilter?.selectedValues
        ?.map((selectedFacetValue) => ({
            label: formatAsLabel(selectedFacetValue),
            onClear: (): Promise<void> | void =>
                dispatch(
                    clearFacetValues(
                        component,
                        facet.name,
                        [selectedFacetValue],
                        getInvalidationsForComponent(component)
                    )
                )
        }))
        .filter((selectedFacet): selectedFacet is SelectedFacet =>
            isDefined(selectedFacet.label)
        );
};

const facetTypeToLabelMapperMap = {
    [FacetType.DEFAULT]: defaultFacetMapper,
    [FacetType.COLORSWATCH]: colorswatchFacetMapper,
    [FacetType.UNIT_RANGE]: unitRangeFacetMapper,
    [FacetType.NUMERIC]: numericFacetMapper,
    [FacetType.HIERARCHICAL]: hierarchicalFacetMapper,
    [FacetType.SHOW_PRODUCTS]: showProductsFacetMapper
} as const;
export const getLabelMapperByFacetType = <TFacetType extends FacetType>(
    facetType: TFacetType
) => facetTypeToLabelMapperMap[facetType] as SelectedFilterMapper<TFacetType>;
